import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import {
  courseHasUnsavedStationChangeAtom,
  CourseMetadataKeys,
  CoursesApi,
  createSuccessResult,
  editFormDataAF,
  EditFormMode,
  errors,
  IApiError,
  ICourseDto,
  ICourseMetadata,
  IRecountStationsResultDto,
  isRideStationsOrderSame,
  IUpdateCourseMetadataCriteria,
  Log,
  logBoolean,
  MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD,
  normalizeCourseMetadata,
  normalizeErrors,
  RebuildRouteTypeEnum,
  RecountCourseStationsCriteriaDto,
  resolveRecountedStationsInfo,
  SimpleResult,
  useSystemParams,
  useTimezone,
} from "@ryde/shared";

import { useCrudEditContext } from "../../contexts";
import { useCourseStationsUpdateInfo } from "./use-course-station-update-info";

export const useCourseDtoActions = () => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { doNotAutoUpdateCourses } = useSystemParams();
  const { mode } = useCrudEditContext<ICourseDto>();
  const [courseDtoGlobal, setCourseDtoGlobal] = useRecoilState<
    Partial<ICourseDto> | undefined
  >(editFormDataAF(["course", mode || EditFormMode.CrudRoute]));
  const setCourseHasUnsavedStationChange = useSetRecoilState(
    courseHasUnsavedStationChangeAtom,
  );

  const { setCourseHasStationChangeComputed } = useCourseStationsUpdateInfo();

  const mergeRouteDateToMetadata = useEvent(
    ({
      recounted,
      sourceStations,
      skipReorderStationsByTime = false,
      rebuildType,
    }: {
      recounted: IRecountStationsResultDto;
      sourceStations: NonNullable<ICourseMetadata["stationsInfo"]>;
      skipReorderStationsByTime?: boolean;
      rebuildType?: RebuildRouteTypeEnum;
    }): void => {
      const resolvedStationsInfo = resolveRecountedStationsInfo({
        recounted: recounted.stationsInfo ?? [],
        source: sourceStations,
        sourceRouteIds: {},
        recountRouteIds: {},
      });

      const keepOrderRebuild = rebuildType === RebuildRouteTypeEnum.KeepOrder;
      const stationsInfo =
        keepOrderRebuild &&
        !isRideStationsOrderSame(resolvedStationsInfo, sourceStations)
          ? sourceStations
          : resolvedStationsInfo;

      const geometryMatchesUiOrder = isRideStationsOrderSame(
        stationsInfo,
        recounted.stationsInfo ?? [],
      );
      const shouldApplyRecountedGeometry =
        geometryMatchesUiOrder || !keepOrderRebuild;

      setCourseDtoGlobal(dto => {
        const currentMetadata: ICourseMetadata = { ...(dto?.metadata || {}) };
        currentMetadata.stationsInfo = stationsInfo;
        if (shouldApplyRecountedGeometry) {
          currentMetadata.totalDistance = recounted.totalDistance;
          currentMetadata.totalDuration = recounted.totalDuration;
          currentMetadata[CourseMetadataKeys.routePolyline] =
            recounted.routePolyline;
          currentMetadata.distances = recounted.distances;
          currentMetadata.durations = recounted.durations;
        } else {
          currentMetadata[CourseMetadataKeys.routePolyline] = undefined;
          currentMetadata.distances = undefined;
          currentMetadata.durations = undefined;
        }
        // Courses use live geo only — no ML path metadata.
        currentMetadata[CourseMetadataKeys.isManualPolylineEdit] = false;
        if (recounted.directionsService) {
          currentMetadata.directionsService = recounted.directionsService;
        }
        const metadataRecounted = normalizeCourseMetadata({
          metadata: currentMetadata,
          reorderStationsByTime: !skipReorderStationsByTime,
          timezone,
        });
        const recountedCourseDto: Partial<ICourseDto> = {
          ...dto,
          metadata: metadataRecounted,
        };
        Log.debug("Course route rebuilt successfully", recountedCourseDto);
        return recountedCourseDto;
      });

      if (keepOrderRebuild) {
        // Mirror ride drag: trust encoded geometry only when it matches the UI station order.
        setCourseHasUnsavedStationChange(!geometryMatchesUiOrder);
      }
    },
  );

  const applyEditingCourseMetadataLocally = useEvent(
    ({
      metadata,
      skipReorderStationsByTime = false,
    }: {
      metadata: ICourseMetadata;
      skipReorderStationsByTime?: boolean;
    }): void => {
      const metadataRecounted = normalizeCourseMetadata({
        metadata,
        reorderStationsByTime: !skipReorderStationsByTime,
        timezone,
      });
      setCourseHasStationChangeComputed(
        { ...courseDtoGlobal?.metadata },
        metadataRecounted,
      );
      setCourseDtoGlobal(courseDto => ({
        ...courseDto,
        metadata: metadataRecounted,
      }));
    },
  );

  const updateCourseMetadataRecounted = useEvent(
    async ({
      metadata,
      optimize = true,
      skipApiCheck = false,
      rebuildType,
      skipReorderStationsByTime = false,
    }: IUpdateCourseMetadataCriteria): Promise<SimpleResult<void>> => {
      const stations = metadata?.stationsInfo || [];

      if (
        !stations?.length ||
        stations.length < MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD ||
        skipApiCheck ||
        doNotAutoUpdateCourses
      ) {
        Log.debug("Course metadata updated without api check", metadata);
        setCourseDtoGlobal(courseDto => ({
          ...courseDto,
          metadata: normalizeCourseMetadata({
            metadata,
            reorderStationsByTime: false,
            timezone,
          }),
        }));
        const skippedApiResult: SimpleResult<void> = new SimpleResult({
          success: true,
        });
        return skippedApiResult;
      }

      Log.debug(
        `Course metadata updating WITH api check (with optimize?- ${logBoolean(optimize)}) `,
        metadata,
      );

      try {
        const criteriaRecount = new RecountCourseStationsCriteriaDto({
          stations,
          optimize,
          rebuildType,
        });
        const recounted = await CoursesApi.recountStationInfo(criteriaRecount);

        if (!recounted?.stationsInfo) {
          const recountFailedResult: SimpleResult<void> = new SimpleResult({
            success: false,
            error: errors.common.stationsNotRecountedSuccessfully,
          });
          return recountFailedResult;
        }

        mergeRouteDateToMetadata({
          recounted,
          sourceStations: stations,
          skipReorderStationsByTime,
          rebuildType,
        });
        const recountSuccessResult: SimpleResult<void> = new SimpleResult({
          success: true,
        });
        return recountSuccessResult;
      } catch (err) {
        return new SimpleResult({
          success: false,
          error: normalizeErrors(err as IApiError, t),
        });
      }
    },
  );

  const updateEditingCourseMetadata = useEvent(
    async ({
      metadata,
      optimize,
      skipApiCheck,
      rebuildType,
      skipReorderStationsByTime,
    }: IUpdateCourseMetadataCriteria): Promise<SimpleResult<void>> => {
      const metadataRecounted = normalizeCourseMetadata({
        metadata,
        reorderStationsByTime: false,
        timezone,
      });
      setCourseHasStationChangeComputed(
        { ...courseDtoGlobal?.metadata },
        metadataRecounted,
      );
      return updateCourseMetadataRecounted({
        metadata: metadataRecounted,
        optimize,
        skipApiCheck,
        rebuildType,
        skipReorderStationsByTime,
      });
    },
  );

  const courseRebuildRouteHandler = useEvent(
    async (
      optimize = true,
    ): Promise<SimpleResult<{ routePolyline?: string }>> => {
      const stations = courseDtoGlobal?.metadata?.stationsInfo || [];

      if (!stations?.length) {
        return new SimpleResult({
          success: false,
          error: "No stations to rebuild",
        });
      }

      const criteriaRecount = new RecountCourseStationsCriteriaDto({
        stations: stations,
        optimize: optimize,
      });

      try {
        const recounted = await CoursesApi.recountStationInfo(criteriaRecount);

        if (!recounted?.stationsInfo) {
          return new SimpleResult({
            success: false,
            error: t(errors.course.stationsNotRecountedSuccessfully),
          });
        }

        if (!courseDtoGlobal) {
          return new SimpleResult({
            success: false,
            error: t(errors.course.invalidCourseEditingSnapshot),
          });
        }

        mergeRouteDateToMetadata({
          recounted,
          sourceStations: stations,
        });

        return createSuccessResult({
          routePolyline: recounted.routePolyline,
        });
      } catch (error) {
        return new SimpleResult({
          error: normalizeErrors(error as IApiError, t),
        });
      }
    },
  );

  return {
    applyEditingCourseMetadataLocally,
    courseRebuildRouteHandler,
    updateEditingCourseMetadata,
  };
};
